<script setup>
import {computed, onMounted} from 'vue'
import LanguageMarquee from "../components/LanguageMarquee.vue";

const props = defineProps({ status: Number, errorId: String, exception: String, exception_code: Number });

const title = computed(() => {
    return {
        503: [
            { lang: 'cs', val: 'Dočasně nedostupné' },
            { lang: 'en', val: 'Temporarily unavailable' },
            { lang: 'ko', val: '일시적으로 사용할 수 없습니다' },
            { lang: 'ns', val: 'Tyaesto-yorān sībaeheanēī' },
            { lang: 'de', val: 'Vorübergehend nicht verfügbar' },
            { lang: 'es', val: 'Temporalmente no disponible' },
            { lang: 'pl', val: 'Tymczasowo niedostępne' },
            { lang: 'zh', val: '暂时无法使用' },
            { lang: 'ja', val: '一時的に利用できません' },
            { lang: 'sk', val: 'Dočasne nedostupné' },
            { lang: 'uk', val: 'Тимчасово недоступно' },
            { lang: 'ru', val: 'Временно недоступно' },
            { lang: 'el', val: 'Προσωρινά μη διαθέσιμο' },
            { lang: 'sv', val: 'Tillfälligt otillgänglig' },
            { lang: 'cy', val: 'Ddim ar gael dros dro' },
            { lang: 'ga', val: 'Níl sé ar fáil go sealadach' },
        ],
        500: [
            { lang: 'cs', val: 'Chyba serveru' },
            { lang: 'en', val: 'Internal server error' },
            { lang: 'ko', val: '서버 오류' },
            { lang: 'ns', val: 'Xibān ēnternēī dea server·n' },
            { lang: 'de', val: 'Interner Serverfehler' },
            { lang: 'es', val: 'Error interno del servidor' },
            { lang: 'pl', val: 'Błąd serwera' },
            { lang: 'zh', val: '服务器内部错误' },
            { lang: 'ja', val: 'サーバー内部エラー' },
            { lang: 'sk', val: 'Chyba servera' },
            { lang: 'uk', val: 'Помилка сервера' },
            { lang: 'ru', val: 'Ошибка сервера' },
            { lang: 'el', val: 'Εσωτερικό σφάλμα διακομιστή' },
            { lang: 'sv', val: 'Internt serverfel' },
            { lang: 'cy', val: 'Gwall gweinydd mewnol' },
            { lang: 'ga', val: 'Earráid inmheánach freastalaí' },
        ],
        404: [
            { lang: 'cs', val: 'Stránka nenalezena' },
            { lang: 'en', val: 'Page not found' },
            { lang: 'ko', val: '페이지를 찾을 수 없습니다' },
            { lang: 'ns', val: 'Piūteas·n sīnaraetteg' },
            { lang: 'de', val: 'Seite nicht gefunden' },
            { lang: 'es', val: 'Página no encontrada' },
            { lang: 'pl', val: 'Stránka nenalezena' },
            { lang: 'zh', val: '找不到页面' },
            { lang: 'ja', val: 'ページが見つかりません' },
            { lang: 'sk', val: 'Stránka sa nenašla' },
            { lang: 'uk', val: 'Сторінку не знайдено' },
            { lang: 'ru', val: 'Страница не найдена' },
            { lang: 'el', val: 'Η σελίδα δεν βρέθηκε' },
            { lang: 'sv', val: 'Sidan kunde inte hittas' },
            { lang: 'cy', val: 'Heb ddod o hyd i’r dudalen' },
            { lang: 'ga', val: 'Leathanach gan aimsiú' },
        ],
        403: [
            { lang: 'cs', val: 'Přístup odepřen' },
            { lang: 'en', val: 'Access denied' },
            { lang: 'ko', val: '접근이 거부되었습니다' },
            { lang: 'ns', val: 'Tyeop·n iriblatteg' },
            { lang: 'de', val: 'Zugriff verweigert' },
            { lang: 'es', val: 'Acceso denegado' },
            { lang: 'pl', val: 'Odmowa dostępu' },
            { lang: 'zh', val: '拒绝访问' },
            { lang: 'ja', val: 'アクセスが拒否されました' },
            { lang: 'sk', val: 'Prístup zamietnutý' },
            { lang: 'uk', val: 'Доступ заборонено' },
            { lang: 'ru', val: 'Доступ запрещён' },
            { lang: 'el', val: 'Άρνηση πρόσβασης' },
            { lang: 'sv', val: 'Åtkomst nekad' },
            { lang: 'cy', val: 'Mynediad wedi ei wrthod' },
            { lang: 'ga', val: 'Rochtain diúltaithe' },
        ],
    }[props.status]
})

const description = computed(() => {
    return {
        503: [
            { lang: 'cs', val: 'Omlouváme se, ale právě probíhá údržba. Zkuste to prosím později.' },
            { lang: 'en', val: 'We’re sorry, maintenance is currently in progress. Please try again later.' },
            { lang: 'ko', val: '죄송합니다. 현재 점검 중입니다. 나중에 다시 시도해 주세요.' },
            { lang: 'ns', val: 'Sōrea, inbaen minitobbann in yoran. Nūmāi atitti āpxa jon.' },
            { lang: 'de', val: 'Wir entschuldigen uns, derzeit finden Wartungsarbeiten statt. Bitte versuchen Sie es später erneut.' },
            { lang: 'es', val: 'Lo sentimos, el mantenimiento está en curso. Por favor, inténtelo de nuevo más tarde.' },
            { lang: 'pl', val: 'Przepraszamy, obecnie trwa konserwacja. Spróbuj ponownie później.' },
            { lang: 'zh', val: '很抱歉，当前正在维护。请稍后再试。' },
            { lang: 'ja', val: '申し訳ありません。ただいまメンテナンス中です。後でもう一度お試しください。' },
            { lang: 'sk', val: 'Ospravedlňujeme sa, prebieha údržba. Skúste to neskôr.' },
            { lang: 'uk', val: 'Вибачте, зараз триває технічне обслуговування. Спробуйте пізніше.' },
            { lang: 'ru', val: 'Извините, в данный момент проводится техническое обслуживание. Повторите попытку позже.' },
            { lang: 'el', val: 'Συγγνώμη, πραγματοποιείται συντήρηση. Δοκιμάστε ξανά αργότερα.' },
            { lang: 'sv', val: 'Vi beklagar, underhåll pågår. Försök igen senare.' },
            { lang: 'cy', val: "Mae'n ddrwg gennym, mae cynnal a chadw ar y gweill. Ceisiwch eto yn nes ymlaen." },
            { lang: 'ga', val: 'Tá brón orainn, tá cothabháil ar siúl faoi láthair. Bain triail eile as níos déanaí.' },
        ],
        500: [
            { lang: 'cs', val: 'Na našem serveru se něco pokazilo. Pracujeme na nápravě.' },
            { lang: 'en', val: 'Something went wrong on our server. We’re working on it.' },
            { lang: 'ko', val: '서버에 문제가 발생했습니다. 해결 중입니다.' },
            { lang: 'ns', val: 'Edob rehīnī on insereu server·n. Inbaen bhwāgīnn on paūton.' },
            { lang: 'de', val: 'Es ist ein Fehler aufgetreten. Wir arbeiten an einer Lösung.' },
            { lang: 'es', val: 'Se ha producido un error. Estamos trabajando para solucionarlo.' },
            { lang: 'pl', val: 'Wystąpił błąd. Pracujemy nad rozwiązaniem.' },
            { lang: 'zh', val: '发生错误。我们正在解决。' },
            { lang: 'ja', val: 'エラーが発生しました。対応中です。' },
            { lang: 'sk', val: 'Nastala chyba. Pracujeme na jej odstránení.' },
            { lang: 'uk', val: 'Сталася помилка. Ми вже працюємо над цим.' },
            { lang: 'ru', val: 'Произошла ошибка. Мы уже работаем над этим.' },
            { lang: 'el', val: 'Παρουσιάστηκε σφάλμα. Το εξετάζουμε.' },
            { lang: 'sv', val: 'Ett fel uppstod. Vi arbetar på det.' },
            { lang: 'cy', val: 'Digwyddodd gwall. Rydym yn gweithio arno.' },
            { lang: 'ga', val: 'Tharla earráid. Táimid ag obair air.' },
        ],
        404: [
            { lang: 'cs', val: 'Požadovanou stránku se nepodařilo najít.' },
            { lang: 'en', val: 'The requested page could not be found.' },
            { lang: 'ko', val: '요청한 페이지를 찾을 수 없습니다.' },
            { lang: 'ns', val: 'Āisokīnī piūteas·n sīmokxā naraett.' },
            { lang: 'de', val: 'Die angeforderte Seite konnte nicht gefunden werden.' },
            { lang: 'es', val: 'No se pudo encontrar la página solicitada.' },
            { lang: 'pl', val: 'Nie można znaleźć żądanej strony.' },
            { lang: 'zh', val: '无法找到请求的页面。' },
            { lang: 'ja', val: 'リクエストされたページが見つかりませんでした。' },
            { lang: 'sk', val: 'Požadovanú stránku sa nepodarilo nájsť.' },
            { lang: 'uk', val: 'Запитану сторінку не вдалося знайти.' },
            { lang: 'ru', val: 'Запрошенная страница не найдена.' },
            { lang: 'el', val: 'Δεν ήταν δυνατή η εύρεση της ζητούμενης σελίδας.' },
            { lang: 'sv', val: 'Den begärda sidan kunde inte hittas.' },
            { lang: 'cy', val: 'Methu dod o hyd i’r dudalen ofynnol.' },
            { lang: 'ga', val: 'Níorbh fhéidir an leathanach iarrtha a aimsiú.' },
        ],
        403: [
            { lang: 'cs', val: 'Na tuto stránku nemáte oprávnění vstoupit.' },
            { lang: 'en', val: 'You are not authorized to access this page.' },
            { lang: 'ko', val: '이 페이지에 접근할 수 있는 권한이 없습니다.' },
            { lang: 'ko', val: 'Dū sīcerī txekteron ana amPiūteas·n kxei.' },
            { lang: 'de', val: 'Sie sind nicht berechtigt, auf diese Seite zuzugreifen.' },
            { lang: 'es', val: 'No tiene autorización para acceder a esta página.' },
            { lang: 'pl', val: 'Nie masz uprawnień do wejścia na tę stronę.' },
            { lang: 'zh', val: '您无权访问此页面。' },
            { lang: 'ja', val: 'このページにアクセスする権限がありません。' },
            { lang: 'sk', val: 'Na túto stránku nemáte oprávnenie vstúpiť.' },
            { lang: 'uk', val: 'У вас немає дозволу на доступ до цієї сторінки.' },
            { lang: 'ru', val: 'У вас нет прав для доступа к этой странице.' },
            { lang: 'el', val: 'Δεν έχετε δικαίωμα πρόσβασης σε αυτήν τη σελίδα.' },
            { lang: 'sv', val: 'Du har inte behörighet att komma åt den här sidan.' },
            { lang: 'cy', val: 'Nid oes gennych hawl i gael mynediad i’r dudalen hon.' },
            { lang: 'ga', val: 'Níl tú údaraithe chun rochtain a fháil ar an leathanach seo.' },
        ],
    }[props.status]
})

</script>

<template>
    <div class="bg-maeum-ebony select-none dark:bg-maeum-black h-dvh w-screen flex flex-col justify-center items-center">
        <div class="container flex flex-col pl-16 pr-6 slide-in-left">
            <div class="bg-light-logo mb-8 dark:bg-dark-logo w-[150px] h-[28px] bg-center bg-no-repeat"></div>
            <h1 class="text-xl font-heading font-bold">
                <language-marquee show-lang :messages="title" changes-title></language-marquee>
            </h1>
            <div>
                <language-marquee delay="0.05s" :messages="description"></language-marquee>
            </div>
            <table class="mt-6 text-sm text-left text-gray-800 dark:text-neutral-50 border border-black dark:border-neutral-300 w-full max-w-lg mb-6 ">
              <tbody>
                <tr class="border-b border-black dark:border-neutral-300">
                  <th class="py-2 px-3 font-semibold w-[140px] text-nowrap text-ellipsis">
                    <language-marquee delay="0.1s" :messages="[
                      { lang: 'cs', val: 'Kód chyby' },
                      { lang: 'en', val: 'Error code' },
                      { lang: 'ko', val: '오류 코드' },
                      { lang: 'ns', val: 'Xibān kxeōd·n' },
                      { lang: 'de', val: 'Fehlercode' },
                      { lang: 'es', val: 'Código de error' },
                      { lang: 'pl', val: 'Kod błędu' },
                      { lang: 'zh', val: '错误代码' },
                      { lang: 'ja', val: 'エラーコード' },
                      { lang: 'sk', val: 'Chybový kód' },
                      { lang: 'uk', val: 'Код помилки' },
                      { lang: 'ru', val: 'Код ошибки' },
                      { lang: 'el', val: 'Κωδικός σφάλματος' },
                      { lang: 'sv', val: 'Felkod' },
                      { lang: 'cy', val: 'Cod gwall' },
                      { lang: 'ga', val: 'Cód earráide' }
                    ]" />
                  </th>
                  <td class="py-2 px-3 font-mono">{{ status }}{{ exception_code }}</td>
                </tr>
                <tr class="border-b border-black dark:border-neutral-300">
                  <th class="py-2 px-3 font-semibold text-nowrap">
                    <language-marquee delay="0.15s" :messages="[
                      { lang: 'cs', val: 'ID události' },
                      { lang: 'en', val: 'Event ID' },
                      { lang: 'ko', val: '이벤트 ID' },
                      { lang: 'ns', val: 'Hwarianain ID' },
                      { lang: 'de', val: 'Ereignis-ID' },
                      { lang: 'es', val: 'ID de evento' },
                      { lang: 'pl', val: 'ID zdarzenia' },
                      { lang: 'zh', val: '事件 ID' },
                      { lang: 'ja', val: 'イベントID' },
                      { lang: 'sk', val: 'ID udalosti' },
                      { lang: 'uk', val: 'ID події' },
                      { lang: 'ru', val: 'ID события' },
                      { lang: 'el', val: 'ID συμβάντος' },
                      { lang: 'sv', val: 'Händelse-ID' },
                      { lang: 'cy', val: 'ID digwyddiad' },
                      { lang: 'ga', val: 'ID imeachta' }
                    ]" />
                  </th>
                  <td class="py-2 px-3 font-mono">{{ errorId }}</td>
                </tr>
              </tbody>
            </table>
        </div>
    </div>
</template>

<style scoped>
.slide-in-left {
    animation: slide-in-left 0.5s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}
/* ----------------------------------------------
 * Generated by Animista on 2025-5-20 23:38:12
 * Licensed under FreeBSD License.
 * See http://animista.net/license for more info.
 * w: http://animista.net, t: @cssanimista
 * ---------------------------------------------- */

/**
 * ----------------------------------------
 * animation slide-in-left
 * ----------------------------------------
 */
@keyframes slide-in-left {
    0% {
        transform: translateX(-20px);
        opacity: 0;
    }
    100% {
        transform: translateX(0);
        opacity: 1;
    }
}

</style>
